(() => {
  const ROOT_SELECTOR = '[data-hood-finder]';

  const track = (event, detail = {}) => {
    if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event, ...detail });
  };

  const parseProducts = (root) => {
    const script = root.querySelector('[data-hood-products]');
    if (!script) return [];
    try {
      const products = JSON.parse(script.textContent);
      return Array.isArray(products) ? products : [];
    } catch (error) {
      console.warn('Hood Finder: product data could not be read.', error);
      return [];
    }
  };

  const getAnswers = (root) => {
    const answers = {};
    root.querySelectorAll('[data-answer]:checked').forEach((input) => {
      answers[input.dataset.answer] = input.value;
    });
    return answers;
  };

  const calculateRecommendation = (root, answers) => {
    const clearance = Number(root.dataset.defaultClearance || 33);
    const lowCfm = Number(root.dataset.lowCfm || 600);
    const mediumCfm = Number(root.dataset.mediumCfm || 1150);
    const highCfm = Number(root.dataset.highCfm || 1260);
    const numericWidth = Number(answers.rangeWidth);
    const hasStandardWidth = Number.isFinite(numericWidth) && numericWidth > 0;
    const recommendedWidth = hasStandardWidth
      ? (answers.installation === 'island' ? numericWidth + 6 : numericWidth)
      : null;
    const installationMap = { wall: 'Wall mount', island: 'Island mount', undercabinet: 'Under-cabinet mount', unsure: 'Layout review needed' };
    const constructionMap = { wall: 'Three visible sides finished', island: 'All four sides finished', undercabinet: 'Cabinet opening must be confirmed', unsure: 'Construction confirmed after layout review' };

    let depth = 'Confirm with Fobest';
    if (answers.installation === 'island') depth = 'Approx. 27 in';
    else if (answers.installation === 'wall' && answers.cooking === 'professional') depth = '24–27 in';
    else if (answers.installation === 'wall') depth = '21–24 in';
    else if (answers.installation === 'undercabinet') depth = 'Confirm cabinet depth';

    let widthNote = 'Final width depends on the confirmed installation layout';
    if (hasStandardWidth && answers.installation === 'wall') widthNote = `Wall-mount starting range: ${numericWidth}–${numericWidth + 6} in`;
    else if (hasStandardWidth && answers.installation === 'island') widthNote = 'Island guidance starts 3–6 in wider than the range';
    else if (hasStandardWidth && answers.installation === 'undercabinet') widthNote = 'Confirm the cabinet opening before choosing the final width';
    else if (hasStandardWidth && answers.installation === 'unsure') widthNote = 'Confirm the mount type before choosing the final width';

    let height = 'Custom review';
    let heightNote = 'Provide the exact ceiling and cooktop measurements';
    const ceilingFeet = Number(answers.ceilingHeight);
    if (Number.isFinite(ceilingFeet) && ceilingFeet > 0) {
      const calculatedHeight = (ceilingFeet * 12) - 36 - clearance;
      height = `Approx. ${calculatedHeight} in`;
      heightNote = `Using a 36 in cooktop height and ${clearance} in mounting clearance`;
    } else if (answers.ceilingHeight === 'sloped') {
      height = 'Custom-cut chimney';
      heightNote = 'A drawing review is recommended for sloped ceilings';
    }

    let cfm = lowCfm;
    if (!recommendedWidth) cfm = null;
    else if (recommendedWidth <= 36) cfm = lowCfm;
    else if (recommendedWidth <= 48) cfm = mediumCfm;
    else cfm = highCfm;
    const ventilationMap = {
      internal: 'Ducted: internal blower',
      remote: 'Ducted: remote blower',
      none: 'Ductless conversion',
      'hood-only': 'Hood only: match your insert',
      unsure: 'Ventilation review needed'
    };
    const ventilationNoteMap = {
      internal: 'The blower motor is installed inside the hood and vents outdoors through ductwork',
      remote: 'The blower motor is installed outside the hood, typically in the attic, and vents outdoors through ductwork',
      none: 'Recirculates air through a ductless conversion with charcoal filtration',
      'hood-only': 'Your insert specifications must be reviewed for compatibility',
      unsure: 'Confirm whether exterior ductwork is available'
    };
    let cfmNote = 'Based on Fobest hood-width blower options';
    if (answers.duct === 'internal') cfmNote = 'Blower is inside the hood; confirm the model and duct layout before ordering';
    else if (answers.duct === 'remote') cfmNote = 'Blower is outside the hood, typically in the attic; confirm the model, duct layout and service access';
    else if (answers.duct === 'none') cfmNote = 'Ductless performance differs and must be confirmed for the kitchen';
    else if (answers.duct === 'hood-only') cfmNote = 'Airflow and compatibility depend on your insert specifications';
    else if (answers.duct === 'unsure') cfmNote = 'Confirm the duct layout before choosing the blower';
    if (['high-heat', 'professional'].includes(answers.cooking)) cfmNote += '; high-output cooking needs a final review';

    return {
      installation: installationMap[answers.installation] || 'Confirm with Fobest',
      construction: constructionMap[answers.installation] || 'To be confirmed',
      width: recommendedWidth ? `${recommendedWidth} in` : 'Custom size review',
      widthNote,
      height,
      heightNote,
      depth,
      cfm: cfm ? `${cfm} CFM starting point` : 'Custom airflow review',
      cfmNote,
      ventilation: ventilationMap[answers.duct] || 'To be confirmed',
      ventilationNote: ventilationNoteMap[answers.duct] || 'Final setup must be confirmed'
    };
  };

  const scoreProducts = (products, answers) => products
    .map((product) => {
      let score = Number(product.priority || 0);
      const shapeMatch = answers.shape !== 'any' && product.shape === answers.shape;
      const materialMatch = answers.finish !== 'any' && product.finish === answers.finish;
      score += answers.shape === 'any' ? 10 : (shapeMatch ? 50 : 0);
      score += answers.finish === 'any' ? 10 : (materialMatch ? 35 : 0);
      return { ...product, score, shapeMatch, materialMatch };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, 3);

  const renderProducts = (root, matches) => {
    const wrapper = root.querySelector('[data-products-wrapper]');
    const grid = root.querySelector('[data-product-results]');
    if (!wrapper || !grid) return;
    grid.replaceChildren();
    if (!matches.length) {
      wrapper.hidden = true;
      return;
    }

    matches.forEach((product, index) => {
      const card = document.createElement('a');
      card.className = 'hood-finder__product-card';
      card.href = product.url;

      if (product.image) {
        const image = document.createElement('img');
        image.className = 'hood-finder__product-image';
        image.src = product.image;
        image.alt = product.title;
        image.loading = 'lazy';
        image.width = 800;
        image.height = 800;
        card.appendChild(image);
      }

      const copy = document.createElement('span');
      copy.className = 'hood-finder__product-copy';
      const badge = document.createElement('span');
      badge.className = 'hood-finder__product-badge';
      if (product.shapeMatch && product.materialMatch) badge.textContent = index === 0 ? 'Best shape + material match' : 'Shape + material match';
      else if (product.shapeMatch) badge.textContent = 'Shape match';
      else if (product.materialMatch) badge.textContent = 'Material match';
      else badge.textContent = 'Recommended design';
      const title = document.createElement('span');
      title.className = 'hood-finder__product-title';
      title.textContent = product.title;
      const price = document.createElement('span');
      price.className = 'hood-finder__product-price';
      price.textContent = product.price ? `Starting at ${product.price}` : 'View design';
      copy.append(badge, title, price);
      card.appendChild(copy);
      grid.appendChild(card);
    });
    wrapper.hidden = false;
  };

  const buildSummary = (answers, recommendation, matches) => {
    const installationLabels = { wall: 'Against a wall', island: 'Kitchen island', undercabinet: 'Under a cabinet', unsure: 'Not sure' };
    const cookingLabels = { everyday: 'Everyday cooking', 'high-heat': 'Frequent high heat', professional: 'Professional-style range' };
    const ductLabels = {
      internal: 'Internal blower inside the hood; ducted outdoors',
      remote: 'Remote blower outside the hood, typically in the attic; ducted outdoors',
      none: 'No exterior duct; ductless conversion',
      'hood-only': 'Using an existing insert',
      unsure: 'Not sure'
    };
    return [
      'FOBEST HOOD FINDER RESULT', '',
      `Cooktop location: ${installationLabels[answers.installation] || answers.installation}`,
      `Range width: ${answers.rangeWidth === 'custom' ? 'Other / custom' : `${answers.rangeWidth} in`}`,
      `Ceiling height: ${['custom', 'sloped'].includes(answers.ceilingHeight) ? answers.ceilingHeight : `${answers.ceilingHeight} ft`}`,
      `Cooking style: ${cookingLabels[answers.cooking] || answers.cooking}`,
      `Blower / ventilation: ${ductLabels[answers.duct] || answers.duct}`,
      `Preferred shape: ${answers.shape}`,
      `Preferred material: ${answers.finish}`, '',
      `Recommended installation: ${recommendation.installation}`,
      `Recommended width: ${recommendation.width}`,
      `Recommended height: ${recommendation.height}`,
      `Recommended depth: ${recommendation.depth}`,
      `Starting airflow: ${recommendation.cfm}`,
      `Blower & ventilation: ${recommendation.ventilation}`, '',
      `Suggested designs: ${matches.length ? matches.map((product) => product.title).join(', ') : 'Products to be selected by Fobest'}`, '',
      'This is an initial planning recommendation. Final dimensions and ventilation requirements must be confirmed before fabrication.'
    ].join('\n');
  };

  const init = (root) => {
    if (!root || root.dataset.hoodFinderInitialized === 'true') return;
    root.dataset.hoodFinderInitialized = 'true';
    const intro = root.querySelector('[data-hood-intro]');
    const quiz = root.querySelector('[data-hood-quiz]');
    const results = root.querySelector('[data-hood-results]');
    const steps = Array.from(root.querySelectorAll('[data-step]'));
    const errorBox = root.querySelector('[data-hood-error]');
    const progressBar = root.querySelector('[data-progress-bar]');
    const progressLabel = root.querySelector('[data-progress-label]');
    const progressResetButton = root.querySelector('.hood-finder__progress-row [data-hood-reset]');
    const backButton = root.querySelector('[data-hood-back]');
    const nextButton = root.querySelector('[data-hood-next]');
    const products = parseProducts(root);
    let currentStep = 1;

    const showStep = (stepNumber) => {
      currentStep = stepNumber;
      steps.forEach((step) => { step.hidden = Number(step.dataset.step) !== currentStep; });
      if (errorBox) errorBox.hidden = true;
      if (progressBar) progressBar.style.width = `${(currentStep / steps.length) * 100}%`;
      if (progressLabel) progressLabel.textContent = `Step ${currentStep} of ${steps.length}`;
      if (progressResetButton) progressResetButton.hidden = currentStep === 1;
      if (backButton) backButton.hidden = currentStep === 1;
      if (nextButton) nextButton.textContent = currentStep === steps.length ? 'See my result' : 'Continue';
      const legend = steps.find((step) => Number(step.dataset.step) === currentStep)?.querySelector('legend');
      if (legend) {
        legend.setAttribute('tabindex', '-1');
        legend.focus({ preventScroll: true });
      }
    };

    const validateStep = () => {
      const activeStep = steps.find((step) => Number(step.dataset.step) === currentStep);
      const required = (activeStep?.dataset.required || '').split(',').filter(Boolean);
      const answers = getAnswers(root);
      const missing = required.some((key) => !answers[key]);
      if (errorBox) errorBox.hidden = !missing;
      return !missing;
    };

    const showResults = () => {
      const answers = getAnswers(root);
      const recommendation = calculateRecommendation(root, answers);
      const matches = scoreProducts(products, answers);
      Object.entries(recommendation).forEach(([key, value]) => {
        root.querySelectorAll(`[data-result="${key}"]`).forEach((element) => { element.textContent = value; });
      });
      renderProducts(root, matches);
      const summaryField = root.querySelector('[data-contact-summary]');
      if (summaryField) summaryField.value = buildSummary(answers, recommendation, matches);
      quiz.hidden = true;
      if (intro) intro.hidden = true;
      results.hidden = false;
      results.scrollIntoView({ behavior: 'smooth', block: 'start' });
      const heading = results.querySelector('h2');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }
      track('hood_finder_complete', { hood_shape: answers.shape, hood_finish: answers.finish, hood_installation: answers.installation, hood_range_width: answers.rangeWidth });
    };

    const reset = () => {
      root.querySelectorAll('[data-answer]').forEach((input) => { input.checked = false; });
      results.hidden = true;
      quiz.hidden = false;
      if (intro) intro.hidden = true;
      currentStep = 1;
      showStep(1);
      quiz.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    root.querySelector('[data-hood-start]')?.addEventListener('click', () => {
      intro.hidden = true;
      results.hidden = true;
      quiz.hidden = false;
      showStep(1);
      quiz.scrollIntoView({ behavior: 'smooth', block: 'start' });
      track('hood_finder_start');
    });
    nextButton?.addEventListener('click', () => {
      if (!validateStep()) return;
      if (currentStep < steps.length) showStep(currentStep + 1);
      else showResults();
    });
    backButton?.addEventListener('click', () => { if (currentStep > 1) showStep(currentStep - 1); });
    root.querySelectorAll('[data-hood-reset]').forEach((button) => button.addEventListener('click', reset));
    root.querySelectorAll('[data-answer]').forEach((input) => input.addEventListener('change', () => { if (errorBox) errorBox.hidden = true; }));
    root.querySelector('form')?.addEventListener('submit', () => track('hood_finder_lead_submit'));

    if (root.querySelector('[data-hood-success]')) {
      if (intro) intro.hidden = true;
      quiz.hidden = true;
      results.hidden = false;
      root.querySelector('[data-specs-wrapper]')?.setAttribute('hidden', '');
      root.querySelector('[data-products-wrapper]')?.setAttribute('hidden', '');
    }
  };

  const initAll = (scope = document) => scope.querySelectorAll(ROOT_SELECTOR).forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
